import {
  Box,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Paper,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
  Card,
  Chip,
  alpha,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../components/toast';
import { getSupportTickets, closeSupportTicket } from '../services/api';
import Scrollbar from '../components/scrollbar';
import SearchBar from '../sections/@dashboard/SearchBar';
import StatsCards from '../components/common/statsCard';
import { LightTooltip } from '../components/custom-components/custom-tooltip';
import Iconify from '../components/iconify';

function DataTable({
  handleClick,
  data,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  setReload,
  loading,
  handleSearch,
  userSelectOpen,
  setUserSelectOpen,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const TABLE_HEAD = [
    { id: 'created', label: 'Created', alignRight: false },
    { id: 'username', label: 'Username', alignRight: false },
    { id: 'subject', label: 'Subject', alignRight: false },
    { id: 'status', label: 'Status', alignRight: false },
    { id: 'last_updated', label: 'Last Updated', alignRight: false },
    { id: '' },
  ];

  const handleMenuOpen = (event, ticket) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTicket(ticket);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTicket(null);
  };

  const handleCloseTicket = async (event) => {
    event.stopPropagation();

    await closeSupportTicket({ id: selectedTicket })
      .then(() => {
        const message = 'Ticket closed successfully.';
        toast.success(message);
      })
      .catch((error) => {
        console.log('error', error);
        toast.error('Unable to close the ticket. Try again later.');
      })
      .finally(() => {
        handleMenuClose();
        setReload((prevReload) => prevReload + 1);
      });
  };

  function formatDate(createdAt) {
    if (!createdAt) return '-';
    const date = new Date(createdAt);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  return (
    <>
      <Box paddingY={2} display={'flex'} columnGap={2} alignItems="center">
        <SearchBar onSearch={handleSearch} />
        <LightTooltip title="Filter tickets by a specific user." disableHoverListener={userSelectOpen}>
          <FormControl sx={{ width: 300 }}>
            <InputLabel id="user-select-label">Select User</InputLabel>
            <Select
              labelId="user-select-label"
              id="user-select"
              label="Select User"
              onOpen={() => setUserSelectOpen(true)}
              onClose={() => setUserSelectOpen(false)}
            >
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </FormControl>
        </LightTooltip>
      </Box>
      <Card sx={{ borderRadius: 2, boxShadow: (theme) => theme.customShadows?.z1 || theme.shadows[1] }}>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  {TABLE_HEAD.map((headCell) => (
                    <TableCell key={headCell.id} align={headCell.alignRight ? 'right' : 'left'}>
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 8 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        Loading support tickets...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 10 }}>
                      <Paper
                        sx={{
                          textAlign: 'center',
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                        }}
                      >
                        <Iconify
                          icon="eva:file-text-outline"
                          sx={{ mb: 2, width: 48, height: 48, color: 'text.disabled' }}
                        />
                        <Typography variant="h6" gutterBottom>
                          No Tickets Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          There are currently no support tickets to display.
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                    <TableRow
                      key={item.id}
                      onClick={() => handleClick(item)}
                      hover
                      sx={{
                        cursor: item.status === 'Closed' ? 'default' : 'pointer',
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox onClick={(e) => e.stopPropagation()} />
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {formatDate(item.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="subtitle2">{item.created_by}</Typography>
                      </TableCell>
                      <TableCell align="left">{item.subject}</TableCell>
                      <TableCell align="left">
                        <Chip
                          label={item.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            fontSize: '0.65rem',
                            backgroundColor: (theme) => {
                              switch (item.status) {
                                case 'Open':
                                  return alpha(theme.palette.warning.main, 0.12);
                                case 'Closed':
                                  return alpha(theme.palette.info.main, 0.12);
                                case 'Answered':
                                  return alpha(theme.palette.success.main, 0.12);
                                default:
                                  return theme.palette.action.selected;
                              }
                            },
                            color: (theme) => {
                              switch (item.status) {
                                case 'Open':
                                  return theme.palette.warning.main;
                                case 'Closed':
                                  return theme.palette.info.main;
                                case 'Answered':
                                  return theme.palette.success.main;
                                default:
                                  return theme.palette.text.primary;
                              }
                            },
                            border: (theme) => {
                              let color;
                              switch (item.status) {
                                case 'Open':
                                  color = theme.palette.warning.main;
                                  break;
                                case 'Closed':
                                  color = theme.palette.info.main;
                                  break;
                                case 'Answered':
                                  color = theme.palette.success.main;
                                  break;
                                default:
                                  color = theme.palette.divider;
                              }
                              return `1px solid ${alpha(color, 0.24)}`;
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {formatDate(item.updated_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <LightTooltip title="Ticket Actions">
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, item.id)}
                            aria-controls="client-actions-menu"
                            aria-haspopup="true"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </LightTooltip>
                        <Menu
                          id="client-actions-menu"
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={handleMenuClose}
                          PaperProps={{
                            sx: {
                              width: 160,
                              boxShadow: 'none',
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                            },
                          }}
                        >
                          <MenuItem onClick={(e) => handleCloseTicket(e)} sx={{ typography: 'body2', py: 1.5 }}>
                            <Iconify icon="eva:close-circle-outline" sx={{ mr: 2 }} />
                            Close Ticket
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
        <TablePagination
          rowsPerPageOptions={[10, 15, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </>
  );
}

export default function SupportTicket() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [supportTickets, setSupportTickets] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userSelectOpen, setUserSelectOpen] = useState(false);

  const handleClick = (item) => {
    if (item.status === 'Closed') return;
    navigate(`/support-tickets/${item.id}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {};

  useEffect(() => {
    setLoading(true);
    getSupportTickets()
      .then((response) => {
        setSupportTickets(response.data.tickets);
        setLoading(false);
        // setReload(false);
      })
      .catch(() => {
        toast.error('Unable to get support tickets, try again later.');
      });
  }, [reload]);

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Support Ticket</title>
      </Helmet>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <Stack marginBottom={7} direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4" gutterBottom>
              Support Tickets Management
            </Typography>
          </Stack>

          <StatsCards
            stats={[
              {
                title: 'Total Support Tickets',
                value: 10,
                icon: <SupportAgentIcon color="primary" />,
              },
              {
                title: 'Opened Support Tickets',
                value: 3,
                icon: <ErrorOutlineIcon color="primary" />,
              },
              {
                title: 'Closed Support Tickets',
                value: 7,
                icon: <DoneAllIcon color="primary" />,
              },
            ]}
          />

          <DataTable
            handleClick={handleClick}
            data={supportTickets}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            setReload={setReload}
            loading={loading}
            handleSearch={handleSearch}
            userSelectOpen={userSelectOpen}
            setUserSelectOpen={setUserSelectOpen}
          />
        </Grid>
      </Grid>
    </>
  );
}
